/**
 * PlayerProfileModal Component
 *
 * Displays another player's public profile including stats,
 * creature gallery preview, and action buttons.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { ActivityStatus } from '@drawn-of-war/shared';
import { CreateBattleModal } from './CreateBattleModal';
import styles from './PlayerProfileModal.module.css';

/**
 * Public profile type (matches backend response)
 */
interface PublicProfile {
  id: string;
  displayName: string;
  presence: {
    status: 'online' | 'offline';
    activity?: ActivityStatus;
    lastSeen?: string;
  };
  stats: {
    wins: number;
    losses: number;
    winRate: number;
    totalBattles: number;
    currentWinStreak: number;
    bestWinStreak: number;
  };
  creatureCount: number;
  memberSince: string;
}

/**
 * Creature preview type
 */
interface CreaturePreview {
  id: string;
  name: string;
  concept: string;
  menuSprite: string;
  createdAt: string;
}

/**
 * Full creature type for detail view
 */
interface CreatureDetail {
  id: string;
  name: string;
  concept: string;
  race: string;
  class: string;
  inputType: string;
  textDescription?: string;
  abilities?: string[];
  sprites: {
    menuSprite: string;
    directions: Record<string, {
      idleFrames?: string[];
      walkFrames?: string[];
      attackFrames?: string[];
    }>;
  };
  combatAttributes: Array<{
    attributeId: string;
    name: string;
    category: string;
    attackType: string;
    damageType: string;
    priority: number;
    effectFrames?: string[];
  }>;
  dynamicAbilities?: Array<{
    id: string;
    name: string;
    description?: string;
    element: string;
    delivery: string;
    cooldown: number;
    range?: { min: number; max: number };
  }>;
  createdAt: string;
  generationTimeMs: number;
  ownerId: string;
}

/**
 * Activity status display config
 * Uses colored dots + text labels for design system alignment
 */
const ACTIVITY_DISPLAY: Record<ActivityStatus, { label: string; dotClass: string }> = {
  browsing: { label: 'Online', dotClass: 'dotOnline' },
  in_lobby: { label: 'In Lobby', dotClass: 'dotLobby' },
  deploying: { label: 'Deploying', dotClass: 'dotDeploying' },
  in_battle: { label: 'In Battle', dotClass: 'dotBattle' },
  creating: { label: 'Creating', dotClass: 'dotCreating' }
};

/**
 * Props for PlayerProfileModal
 */
interface PlayerProfileModalProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Optional: battleId if the current user is hosting a battle */
  currentBattleId?: string;
  onTrade?: () => void;
}

/**
 * PlayerProfileModal Component
 */
export function PlayerProfileModal({
  userId,
  isOpen,
  onClose,
  currentBattleId,
  onTrade
}: PlayerProfileModalProps) {
  const { getIdToken } = useAuth();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [creatures, setCreatures] = useState<CreaturePreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGallery, setShowGallery] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const fetchProfileData = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      // Fetch profile and creatures in parallel
      const [profileRes, creaturesRes] = await Promise.all([
        fetch(`${apiUrl}/api/users/${id}/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${apiUrl}/api/users/${id}/creatures?limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!profileRes.ok) {
        throw new Error('Failed to load profile');
      }

      const profileData = await profileRes.json();
      setProfile(profileData);

      if (creaturesRes.ok) {
        const creaturesData = await creaturesRes.json();
        setCreatures(creaturesData.creatures || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  // Open challenge modal (shows create battle form)
  const handleChallenge = useCallback(() => {
    setShowChallengeModal(true);
  }, []);

  // Close challenge modal
  const handleCloseChallengeModal = useCallback(() => {
    setShowChallengeModal(false);
  }, []);

  // Handle successful challenge creation
  const handleChallengeSuccess = useCallback(() => {
    // Close both modals on success
    setShowChallengeModal(false);
    onClose();
  }, [onClose]);

  // Send a lobby invite (only available if hosting a battle)
  const handleInvite = useCallback(async () => {
    if (!currentBattleId) return;

    setInviteLoading(true);
    setInviteError(null);
    setInviteSuccess(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/invites/lobby`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          targetUserId: userId,
          battleId: currentBattleId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invite');
      }

      setInviteSuccess('Invite sent! Waiting for response...');
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setInviteLoading(false);
    }
  }, [userId, currentBattleId, getIdToken]);

  useEffect(() => {
    if (isOpen && userId) {
      fetchProfileData(userId);
      // Reset invite states when opening
      setInviteSuccess(null);
      setInviteError(null);
    }
  }, [isOpen, userId, fetchProfileData]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const activityInfo = profile?.presence.activity
    ? ACTIVITY_DISPLAY[profile.presence.activity]
    : ACTIVITY_DISPLAY.browsing;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
          ✕
        </button>

        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading profile...</p>
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <p>{error}</p>
            <button onClick={() => fetchProfileData(userId)}>Retry</button>
          </div>
        ) : profile ? (
          <>
            {/* Profile Header */}
            <header className={styles.header}>
              <div className={styles.avatar}>👤</div>
              <div className={styles.headerInfo}>
                <h2 className={styles.displayName}>{profile.displayName}</h2>
                <span className={`${styles.status} ${styles[profile.presence.status]}`}>
                  <span className={`${styles.statusDot} ${styles[activityInfo.dotClass]}`} />
                  {activityInfo.label}
                </span>
              </div>
            </header>

            {/* Stats Section */}
            <section className={styles.statsSection}>
              <h3 className={styles.sectionTitle}>Battle Stats</h3>
              <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                  <span className={`${styles.statValue} ${styles.wins}`}>{profile.stats.wins}</span>
                  <span className={styles.statLabel}>Wins</span>
                </div>
                <div className={styles.statItem}>
                  <span className={`${styles.statValue} ${styles.losses}`}>{profile.stats.losses}</span>
                  <span className={styles.statLabel}>Losses</span>
                </div>
                <div className={styles.statItem}>
                  <span className={`${styles.statValue} ${styles.winRate}`}>{profile.stats.winRate}%</span>
                  <span className={styles.statLabel}>Win Rate</span>
                </div>
              </div>
              <div className={styles.statsSecondary}>
                <span>Total Battles: {profile.stats.totalBattles}</span>
                <span>Current Streak: {profile.stats.currentWinStreak}</span>
                <span>Best Streak: {profile.stats.bestWinStreak}</span>
              </div>
            </section>

            {/* Creatures Section */}
            <section className={styles.creaturesSection}>
              <div className={styles.creaturesHeader}>
                <h3 className={styles.sectionTitle}>
                  Creatures ({profile.creatureCount})
                </h3>
                {profile.creatureCount > 0 && (
                  <button
                    className={styles.viewGalleryButton}
                    onClick={() => setShowGallery(true)}
                  >
                    View Gallery
                  </button>
                )}
              </div>

              {creatures.length === 0 ? (
                <p className={styles.noCreatures}>No creatures yet</p>
              ) : (
                <div className={styles.creaturePreview}>
                  {creatures.map(creature => (
                    <div key={creature.id} className={styles.creatureCard}>
                      {creature.menuSprite ? (
                        <img
                          src={creature.menuSprite}
                          alt={creature.name}
                          className={styles.creatureSprite}
                        />
                      ) : (
                        <div className={styles.creaturePlaceholder}>🐾</div>
                      )}
                      <span className={styles.creatureName}>{creature.name}</span>
                    </div>
                  ))}
                  {profile.creatureCount > 5 && (
                    <div className={styles.moreCreatures}>
                      +{profile.creatureCount - 5} more
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Invite Feedback */}
            {inviteSuccess && (
              <div className={styles.inviteSuccess}>
                {inviteSuccess}
              </div>
            )}
            {inviteError && (
              <div className={styles.inviteError}>
                {inviteError}
              </div>
            )}

            {/* Action Buttons */}
            <section className={styles.actions}>
              <button
                className={`${styles.actionButton} ${styles.challenge}`}
                onClick={handleChallenge}
                disabled={inviteLoading}
                title="Challenge to battle"
              >
                {inviteLoading ? '...' : '⚔️ Challenge'}
              </button>
              <button
                className={`${styles.actionButton} ${styles.invite}`}
                onClick={handleInvite}
                disabled={inviteLoading || !currentBattleId}
                title={currentBattleId ? 'Invite to your lobby' : 'Create a battle first to invite players'}
              >
                📨 Invite
              </button>
              <button
                className={`${styles.actionButton} ${styles.trade}`}
                onClick={onTrade}
                disabled={!onTrade}
                title="Propose trade (coming soon)"
              >
                🔄 Trade
              </button>
            </section>
          </>
        ) : null}

        {/* Gallery Modal (nested) */}
        {showGallery && profile && (
          <PlayerGalleryModal
            userId={userId}
            displayName={profile.displayName}
            onClose={() => setShowGallery(false)}
          />
        )}

        {/* Challenge Modal (Create Battle with challenge target) */}
        {profile && (
          <CreateBattleModal
            isOpen={showChallengeModal}
            onClose={handleCloseChallengeModal}
            challengeTarget={{
              userId: userId,
              displayName: profile.displayName
            }}
            onSuccess={handleChallengeSuccess}
          />
        )}
      </div>
    </div>
  );
}

/**
 * PlayerGalleryModal Component
 *
 * Full gallery view of a player's creatures with pagination.
 */
interface PlayerGalleryModalProps {
  userId: string;
  displayName: string;
  onClose: () => void;
}

function PlayerGalleryModal({ userId, displayName, onClose }: PlayerGalleryModalProps) {
  const { getIdToken } = useAuth();
  const [creatures, setCreatures] = useState<CreaturePreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [selectedCreatureId, setSelectedCreatureId] = useState<string | null>(null);

  const fetchCreatures = useCallback(async (cursor?: string) => {
    const isLoadMore = !!cursor;
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const url = cursor
        ? `${apiUrl}/api/users/${userId}/creatures?limit=20&cursor=${cursor}`
        : `${apiUrl}/api/users/${userId}/creatures?limit=20`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (isLoadMore) {
          setCreatures(prev => [...prev, ...data.creatures]);
        } else {
          setCreatures(data.creatures);
        }
        setNextCursor(data.nextCursor);
        setTotal(data.total);
      }
    } catch (err) {
      console.error('Failed to fetch creatures:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId, getIdToken]);

  useEffect(() => {
    fetchCreatures();
  }, [fetchCreatures]);

  return (
    <div className={styles.galleryOverlay} onClick={onClose}>
      <div className={styles.galleryModal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.galleryHeader}>
          <h2>{displayName}'s Creatures ({total})</h2>
          <button className={styles.closeButton} onClick={onClose}>✕</button>
        </header>

        <div className={styles.galleryContent}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Loading creatures...</p>
            </div>
          ) : creatures.length === 0 ? (
            <p className={styles.noCreatures}>No creatures found</p>
          ) : (
            <>
              <div className={styles.galleryGrid}>
                {creatures.map(creature => (
                  <button
                    key={creature.id}
                    className={styles.galleryCard}
                    onClick={() => setSelectedCreatureId(creature.id)}
                    type="button"
                  >
                    {creature.menuSprite ? (
                      <img
                        src={creature.menuSprite}
                        alt={creature.name}
                        className={styles.gallerySprite}
                      />
                    ) : (
                      <div className={styles.galleryPlaceholder}>🐾</div>
                    )}
                    <span className={styles.galleryCreatureName}>{creature.name}</span>
                  </button>
                ))}
              </div>

              {nextCursor && (
                <button
                  className={styles.loadMoreButton}
                  onClick={() => fetchCreatures(nextCursor)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : `Load More (${total - creatures.length} remaining)`}
                </button>
              )}
            </>
          )}
        </div>

        {/* Creature Detail Modal (nested) */}
        {selectedCreatureId && (
          <CreatureDetailModal
            creatureId={selectedCreatureId}
            onClose={() => setSelectedCreatureId(null)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * CreatureDetailModal Component
 *
 * Shows full creature details for another player's creature.
 */
interface CreatureDetailModalProps {
  creatureId: string;
  onClose: () => void;
}

function CreatureDetailModal({ creatureId, onClose }: CreatureDetailModalProps) {
  const { getIdToken } = useAuth();
  const [creature, setCreature] = useState<CreatureDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Animation state
  const [selectedDirection, setSelectedDirection] = useState<'E' | 'NE' | 'SE' | 'W' | 'NW' | 'SW'>('SE');
  const [animationType, setAnimationType] = useState<'idle' | 'walk' | 'attack'>('idle');
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  // Effect animation state
  const [effectFrameIndex, setEffectFrameIndex] = useState<Record<string, number>>({});

  const ANIMATION_FPS = 8;

  const fetchCreature = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const res = await fetch(`${apiUrl}/api/creature-library/${creatureId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error('Failed to load creature');
      }

      const data = await res.json();
      setCreature(data.creature);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load creature');
    } finally {
      setLoading(false);
    }
  }, [creatureId, getIdToken]);

  useEffect(() => {
    fetchCreature();
  }, [fetchCreature]);

  // Get current animation frames
  const getCurrentFrames = useCallback((): string[] => {
    if (!creature) return [];

    const isMirrored = selectedDirection === 'W' || selectedDirection === 'NW' || selectedDirection === 'SW';
    const baseDirection = isMirrored
      ? (selectedDirection === 'W' ? 'E' : selectedDirection === 'NW' ? 'NE' : 'SE')
      : selectedDirection;

    const directionData = creature.sprites.directions[baseDirection];
    if (!directionData) return [];

    if (animationType === 'walk') {
      return directionData.walkFrames || [];
    } else if (animationType === 'attack') {
      return directionData.attackFrames || [];
    } else {
      return directionData.idleFrames || [];
    }
  }, [creature, selectedDirection, animationType]);

  // Animation loop
  useEffect(() => {
    if (!creature || !isPlaying) return;

    const frames = getCurrentFrames();
    if (frames.length === 0) return;

    const interval = setInterval(() => {
      setCurrentFrame(prev => (prev + 1) % frames.length);
    }, 1000 / ANIMATION_FPS);

    return () => clearInterval(interval);
  }, [creature, isPlaying, getCurrentFrames]);

  // Effect animation loop
  useEffect(() => {
    if (!creature) return;

    const attributesWithEffects = creature.combatAttributes.filter(
      attr => attr.effectFrames && attr.effectFrames.length > 0
    );

    if (attributesWithEffects.length === 0) return;

    const interval = setInterval(() => {
      setEffectFrameIndex(prev => {
        const next = { ...prev };
        for (const attr of attributesWithEffects) {
          const frameCount = attr.effectFrames?.length || 1;
          next[attr.attributeId] = ((prev[attr.attributeId] || 0) + 1) % frameCount;
        }
        return next;
      });
    }, 1000 / ANIMATION_FPS);

    return () => clearInterval(interval);
  }, [creature]);

  const getCurrentFrame = (): string => {
    const frames = getCurrentFrames();
    if (frames.length === 0) return creature?.sprites.menuSprite || '';
    return frames[currentFrame % frames.length] || creature?.sprites.menuSprite || '';
  };

  const isMirrored = selectedDirection === 'W' || selectedDirection === 'NW' || selectedDirection === 'SW';

  return (
    <div className={styles.detailOverlay} onClick={onClose}>
      <div className={styles.detailModal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>✕</button>

        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading creature...</p>
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <p>{error}</p>
            <button onClick={fetchCreature}>Retry</button>
          </div>
        ) : creature ? (
          <div className={styles.detailContent}>
            {/* Header */}
            <header className={styles.detailHeader}>
              <h2>{creature.name}</h2>
              <p className={styles.detailConcept}>{creature.concept}</p>
            </header>

            <div className={styles.detailBody}>
              {/* Animation Viewer */}
              <div className={styles.animationViewer}>
                <div className={styles.animationCanvas}>
                  <img
                    src={getCurrentFrame()}
                    alt={creature.name}
                    className={`${styles.spriteImage} ${isMirrored ? styles.mirrored : ''}`}
                  />
                </div>

                {/* Animation Controls */}
                <div className={styles.animationControls}>
                  <div className={styles.controlGroup}>
                    <span className={styles.controlLabel}>Animation:</span>
                    <div className={styles.buttonGroup}>
                      {(['idle', 'walk', 'attack'] as const).map(type => (
                        <button
                          key={type}
                          className={`${styles.controlButton} ${animationType === type ? styles.active : ''}`}
                          onClick={() => { setAnimationType(type); setCurrentFrame(0); }}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={styles.controlGroup}>
                    <span className={styles.controlLabel}>Direction:</span>
                    <div className={styles.directionGrid}>
                      {(['NW', 'NE', 'W', 'E', 'SW', 'SE'] as const).map(dir => (
                        <button
                          key={dir}
                          className={`${styles.directionButton} ${selectedDirection === dir ? styles.active : ''}`}
                          onClick={() => { setSelectedDirection(dir); setCurrentFrame(0); }}
                        >
                          {dir}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    className={styles.playPauseButton}
                    onClick={() => setIsPlaying(!isPlaying)}
                  >
                    {isPlaying ? 'Pause' : 'Play'}
                  </button>
                </div>
              </div>

              {/* Creature Info */}
              <div className={styles.creatureInfo}>
                {/* Basic Info */}
                <section className={styles.infoSection}>
                  <h3>Basic Info</h3>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Race:</span>
                      <span className={styles.infoValue}>{creature.race}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Class:</span>
                      <span className={styles.infoValue}>{creature.class}</span>
                    </div>
                  </div>
                </section>

                {/* Combat Abilities */}
                {creature.combatAttributes.length > 0 && (
                  <section className={styles.infoSection}>
                    <h3>Combat Abilities</h3>
                    <div className={styles.abilitiesList}>
                      {creature.combatAttributes.map(attr => (
                        <div key={attr.attributeId} className={styles.abilityCard}>
                          <div className={styles.abilityInfo}>
                            <span className={styles.abilityName}>{attr.name}</span>
                            <div className={styles.abilityBadges}>
                              <span className={styles.badge}>{attr.category}</span>
                              <span className={styles.badge}>{attr.attackType}</span>
                              <span className={styles.badge}>{attr.damageType}</span>
                            </div>
                          </div>
                          {attr.effectFrames && attr.effectFrames.length > 0 && (
                            <div className={styles.effectPreview}>
                              <img
                                src={`data:image/png;base64,${attr.effectFrames[effectFrameIndex[attr.attributeId] || 0]}`}
                                alt={`${attr.name} effect`}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Dynamic Abilities */}
                {creature.dynamicAbilities && creature.dynamicAbilities.length > 0 && (
                  <section className={styles.infoSection}>
                    <h3>Abilities</h3>
                    <div className={styles.abilitiesList}>
                      {creature.dynamicAbilities.map((ability, index) => (
                        <div key={ability.id || index} className={styles.abilityCard}>
                          <div className={styles.abilityInfo}>
                            <span className={styles.abilityName}>{ability.name}</span>
                            {ability.description && (
                              <p className={styles.abilityDescription}>{ability.description}</p>
                            )}
                            <div className={styles.abilityBadges}>
                              {ability.element && <span className={styles.badge}>{ability.element}</span>}
                              {ability.delivery && <span className={styles.badge}>{ability.delivery}</span>}
                              <span className={styles.badge}>CD: {ability.cooldown}s</span>
                              {ability.range && (
                                <span className={styles.badge}>Range: {ability.range.min}-{ability.range.max}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Detected Abilities */}
                {creature.abilities && creature.abilities.length > 0 && (
                  <section className={styles.infoSection}>
                    <h3>Detected Traits</h3>
                    <div className={styles.traitTags}>
                      {creature.abilities.map((ability, index) => (
                        <span key={index} className={styles.traitTag}>{ability}</span>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
